import React, { useCallback } from 'react';
import { Link, Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';

import { CMEMS } from './CMEMS.js';
import { DTU10 } from './DTU10.js';
import { HYCOM } from './HYCOM.js';
import { HYCOM_V02 } from './HYCOM_V02.js';
import { NOAA } from './NOAA.js';
import { ERA5 } from './ERA5';
import { DHIGWM } from './DHIGWM.js';
import { ERA5WAVEMODELLING } from './ERA5WAVEMODELLING'
import { ProviderNames } from '../ProviderDialog';

Font.register({
  family: 'Roboto',
  src: 'https://fonts.googleapis.com/css?family=Roboto&display=swap',
});

const styles = StyleSheet.create({
  container: {
    padding: 20,
    backgroundColor: '#F2F5F7' as any,
  },
  paragraph: { color: '#0B4566', fontSize: 10.5, padding: 3 },
  title: {
    color: '#0B4566',
    fontWeight: 'extrabold',
    fontSize: 16,
    paddingBottom: 7,
  },
  table: {
    display: 'table' as any,
    width: 'auto',
    color: '#0B4566',
    marginTop: 15,
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row',
  },
  link: {
    color: '#00A4EC',
  },
  tableColHeader: {
    width: '50%',
    borderBottomColor: '#0B4566',
    borderBottomWidth: 1,
  },
  tableCol: {
    width: '50%',
    borderBottomColor: 'lightgrey',
    borderBottomWidth: 1,
  },
  tableCellHeader: {
    padding: 7,
    fontSize: 12,
    fontWeight: 'heavy',
  },
  tableCellHeaderHidden: {
    color: 'transparent',
  },
  tableCell: {
    padding: 6,
    fontSize: 10.5,
    fontWeight: 'extralight',
  },
});

interface ParentCompProps {
  providerName: string;
}

const PdfConverter: React.FC<ParentCompProps> = ({ providerName }: ParentCompProps) => {
  const buildHeader = (entry: any) => {
    return entry[1].value.header.map((col: any, index: number) => {
      return (
        <View key={index} style={styles.tableColHeader}>
          <Text
            style={
              entry[1].value.hiddenHeaders && entry[1].value.hiddenHeaders.includes(col)
                ? styles.tableCellHeaderHidden
                : styles.tableCellHeader
            }>
            {col}
          </Text>
        </View>
      );
    });
  };

  const formatLink = (input: string) => {
    const split = input.split('***');
    return (
      <View style={styles.tableCell}>
        <Text>{split[0]}</Text>
        <Link style={styles.link} src={split[1]}>
          {split[1]}
        </Link>
        <Text>{split[2]}</Text>
        <Link style={styles.link} src={split[3]}>
          {split[3]}
        </Link>
        <Text>{split[4]}</Text>
        <Link style={styles.link} src={split[5]}>
          {split[5]}
        </Link>
      </View>
    );
  };

  const buildBody = (entry: any) => {
    return entry[1].value.rows.map((row: any, i: number) => {
      return (
        <View key={i} style={styles.tableRow}>
          {entry[1].value.header.map((cell: any, index: number) => {
            return (
              <View key={index} style={styles.tableCol}>
                {row[cell].includes('***') ? (
                  formatLink(row[cell])
                ) : (
                  <Text style={styles.tableCell}>{row[cell]}</Text>
                )}
              </View>
            );
          })}
        </View>
      );
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getPdfElement = (entry: any, i: number) => {
    // Prop 'key' required to avoid React error:
    if (entry[0].includes('paragraph')) {
      return (
        <View key={i}>
          <Text style={styles.paragraph}>{entry[1].value}</Text>
        </View>
      );
    } else if (entry[0].includes('title')) {
      return (
        <View key={i}>
          <Text style={styles.title}>{entry[1].value}</Text>
        </View>
      );
    } else if (entry[0].includes('table')) {
      return (
        <View style={styles.table} key={i}>
          <View style={styles.tableRow}>{buildHeader(entry)}</View>
          {buildBody(entry)}
        </View>
      );
    } else if (entry[0].includes('link')) {
      return (
        <View key={i}>
          <Link src="www.dhigroup.net" />
        </View>
      );
    } else {
      return (
        <View key={i}>
          <Text>{entry[1].value}</Text>
        </View>
      );
    }
  };

  const getPdfContent = (providerName: string) => {
    switch (providerName) {
      case ProviderNames.ERA5_WAVEMODELLING:
      case ProviderNames.ERA5_WAVEMODELLING_DEV:
        const content = Object.entries(ERA5WAVEMODELLING as any).map((entry: any, i: number) => {
          const el = getPdfElement(entry as any, i);
          return el;
        });
        return content;
      case ProviderNames.DTU10: {
        const content = Object.entries(DTU10 as any).map((entry: any, i: number) => {
          const el = getPdfElement(entry as any, i);
          return el;
        });
        return content;
      }
      case ProviderNames.CMEMS: {
        const content = Object.entries(CMEMS as any).map((entry: any, i: number) => {
          const el = getPdfElement(entry as any, i);
          return el;
        });
        return content;
      }
      case ProviderNames.HYCOM: {
        const content = Object.entries(HYCOM as any).map((entry: any, i: number) => {
          const el = getPdfElement(entry as any, i);
          return el;
        });
        return content;
      }
      case ProviderNames.HYCOM_V02: {
        const content = Object.entries(HYCOM_V02 as any).map((entry: any, i: number) => {
          const el = getPdfElement(entry as any, i);
          return el;
        });
        return content;
      }
      case ProviderNames.NOAA: {
        const content = Object.entries(NOAA as any).map((entry: any, i: number) => {
          const el = getPdfElement(entry as any, i);
          return el;
        });
        return content;
      }
      case ProviderNames.ERA5:
      case ProviderNames.ERA5_MOOD: {
        const content = Object.entries(ERA5 as any).map((entry: any, i: number) => {
          const el = getPdfElement(entry as any, i);
          return el;
        });
        return content;
      }      
      default:
        if (providerName.startsWith(ProviderNames.DHIGWM)){
          const content = Object.entries(DHIGWM as any).map((entry: any, i: number) => {
            const el = getPdfElement(entry as any, i);
            return el;
          });
          return content;
        }
        else{
          return null;
        }        
    }
  };

  const formatToPDF = useCallback(() => {
    return (
      <Document>
        <Page style={styles.container}>{getPdfContent(providerName)}</Page>
      </Document>
    );
  }, [getPdfElement]);

  return <>{formatToPDF()}</>;
};

export default PdfConverter;
